import React, { useMemo, useRef, useState } from 'react';
import { Check, Copy, LayoutGrid } from 'lucide-react';
import { Modal } from '@ofself/shell';
import { Section, Decide, RealmChooser } from '../../components/grant/parts';
import { ConsentFrame } from '../../components/nucleus/GrantViews';
import Segmented from '../../components/nucleus/Segmented';
import AccessList from '../../components/nucleus/AccessList';
import { TokenFace, Glyphed } from '../../components/nucleus/faces';
import { API_BASE } from '../../services/apiConfig';
import { dateOf } from '../../nucleus/format';

// MAKING AN ACCESS TOKEN, in the consent page's parts: a token grants, so it is made
// the way a realm is (GRAMMAR.md, "Making or changing anything that grants"), and it
// asks what the consent page asks: the realms it reaches (one or more, their union),
// what it may do there, until when. Then the token, shown this once, and how to hand
// it to Claude Code.
//   POST /access-tokens {name, realm_ids, verbs, expires_in_hours, group_id?}
// readme_library/decision_designs_ledger/2026-10-31_access-tokens.md

// the node verbs, in the consent page's order (components/grant/Ask.js KEY_ORDER)
export const TOKEN_VERBS = ['read', 'edit', 'create', 'delete', 'propose'];
// each verb in words, said of the nodes in its realms ("It may see them, …")
const VERB_SAYS = { read: 'see them', edit: 'change them', create: 'add new ones', delete: 'remove them', propose: 'suggest changes, which apply once approved' };
const listOf = (xs, and = 'and') => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} ${and} ${xs[xs.length - 1]}`);
// The consent page's terms (components/grant/consent.js EXPIRY), and two short ones
// for a one-off. Never "Never expires": a token is a secret pasted somewhere, so it
// always lapses. The API allows a year at most.
export const TOKEN_TERMS = [
    { id: 1, label: '1 hour' },
    { id: 24, label: '1 day' },
    { id: 168, label: '7 days' },
    { id: 720, label: '30 days' },
    { id: 2160, label: '3 months' },
    { id: 8760, label: '1 year' },
];
const DEFAULT_TERM = 720;
const HUE = 'var(--nx-zone-gates)';

// The node verbs a realm allows, in order: what a token on it can be given.
export const verbsOfRealm = (realm) => TOKEN_VERBS.filter((v) => realm?.ceilingBlob?.nodes && v in realm.ceilingBlob.nodes);
// ...and of several, which a grant folds into one
const verbsOfAll = (list) => TOKEN_VERBS.filter((v) => list.some((r) => verbsOfRealm(r).includes(v)));
// What it reaches through one realm: the verbs it may use there, each with the
// realm's own scope, as the backend grants them (realms fold into their union).
const reachOf = (realm, verbs) => {
    const nodes = realm?.ceilingBlob?.nodes || {};
    const kept = verbs.filter((v) => v in nodes);
    return kept.length ? { nodes: Object.fromEntries(kept.map((v) => [v, nodes[v]])) } : {};
};

// A realm a token may be made on: one of the space's own that holds nodes. Never a
// realm made for one recipient (theirs alone), the group's No access, or the null realm.
export const tokenRealms = (realms) => (realms || []).filter((r) => !r.shared && !r.nullRealm
    && r.builtin !== 'no_access' && verbsOfRealm(r).length > 0);

// the API's address as a script outside the browser must write it
const apiBase = () => (API_BASE.startsWith('/') ? `${window.location.origin}${API_BASE}` : API_BASE);

export default function TokenMaker({ realms, schemaNames, preset = {}, groupId, spaceName, calls, onDone }) {
    const inGroup = !!groupId;
    const offered = useMemo(() => tokenRealms(realms), [realms]);
    const byId = (ids) => ids.map((id) => offered.find((r) => r.id === id)).filter(Boolean);
    const [name, setName] = useState(preset.name || '');
    const [realmIds, setRealmIds] = useState(() => {
        const asked = (preset.realmIds || []).filter((id) => offered.some((r) => r.id === id));
        return asked.length ? asked : offered.length === 1 ? [offered[0].id] : [];
    });
    const picked = byId(realmIds);
    const allowed = verbsOfAll(picked);
    const [verbs, setVerbs] = useState(() => new Set(verbsOfAll(byId(realmIds))));
    const [hours, setHours] = useState(DEFAULT_TERM);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    const [made, setMade] = useState(null);           // { token, access_token } once made
    const [at, setAt] = useState(1);
    const scroller = useRef(null);

    // pressing a realm adds it, pressing an added one takes it off (the consent page's
    // RealmChooser). A verb a newly added realm brings comes on; one you tapped off stays off.
    const toggleRealm = (id) => {
        const next = realmIds.includes(String(id)) ? realmIds.filter((x) => x !== String(id)) : [...realmIds, String(id)];
        const before = verbsOfAll(byId(realmIds));
        const after = verbsOfAll(byId(next));
        setVerbs((s) => new Set(after.filter((v) => s.has(v) || !before.includes(v))));
        setRealmIds(next);
    };
    const toggle = (v) => setVerbs((s) => {
        const n = new Set(s);
        if (n.has(v)) n.delete(v); else n.add(v);
        return n;
    });
    const chosen = allowed.filter((v) => verbs.has(v));
    // the four everyday verbs always stand, held when its realms do not allow one;
    // propose only where a realm allows it
    const offeredVerbs = TOKEN_VERBS.filter((v) => v !== 'propose' || allowed.includes(v));
    const barred = offeredVerbs.filter((v) => !allowed.includes(v));
    const term = TOKEN_TERMS.find((t) => t.id === hours) || TOKEN_TERMS[3];
    const until = new Date(Date.now() + hours * 3_600_000);
    const shown = name.trim();
    const realmsLine = picked.map((r) => r.name).join(' + ');
    const ready = !!shown && picked.length > 0 && chosen.length > 0;
    const space = inGroup ? `${spaceName}’s` : 'your';

    const outline = made ? [] : [
        { n: 1, title: 'Name it', done: !!shown, say: shown || 'Not named yet' },
        { n: 2, title: 'Realms', done: picked.length > 0, say: realmsLine || 'Not chosen yet' },
        { n: 3, title: 'What it may do', done: chosen.length > 0, say: chosen.length ? chosen.join(', ') : 'Nothing yet' },
        { n: 4, title: 'How long', done: true, say: term.label },
        { n: 5, title: 'Check it', done: ready, say: ready ? 'Ready to make' : !shown ? 'Name it first' : !picked.length ? 'Choose its realms' : 'Choose what it may do' },
    ];
    const goTo = (n) => {
        setAt(n);
        scroller.current?.querySelector(`[data-step="${n}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };
    // which step is in view: the last whose top has passed the top of the scroll
    const spy = () => {
        const box = scroller.current;
        if (!box) return;
        const steps = [...box.querySelectorAll('[data-step]')];
        const top = box.scrollTop + 48;
        const seen = steps.filter((el) => el.offsetTop <= top).pop();
        if (seen) setAt(Number(seen.dataset.step));
    };

    const make = async () => {
        if (!ready || working) return;
        setWorking(true);
        setError(null);
        try {
            const out = await calls.mintToken({ name: shown, realm_ids: realmIds, verbs: chosen, expires_in_hours: hours });
            setMade(out);
            scroller.current?.scrollTo?.({ top: 0 });
        } catch (e) {
            setError(e?.message || 'The token could not be made.');
        } finally {
            setWorking(false);
        }
    };
    const close = () => onDone(!!made);

    return (
        <Modal label={made ? `Copy ${shown}’s token` : 'Make a token'} onClose={close} className="re tk">
            <ConsentFrame>
                <div className="re__body" style={{ '--re-hue': HUE }}>
                    <nav className="re__outline" aria-label="Steps">
                        <span className="re__outline-head">{made ? 'Made' : 'Making a token'}</span>
                        <ol>
                            {(made ? [{ n: 6, title: 'Copy it', done: false, say: 'Shown this once' }] : outline).map((x) => (
                                <li key={x.n}>
                                    <button type="button" aria-current={(made || at === x.n) ? 'step' : undefined}
                                        className={`re__outline-step${x.done ? ' is-done' : ''}`} onClick={() => !made && goTo(x.n)}>
                                        <span className="re__mark" aria-hidden="true">{x.done && <Check size={11} strokeWidth={2.4} />}</span>
                                        <span className="re__outline-text"><b>{x.title}</b><small>{x.say}</small></span>
                                        <span className="sr-only">{x.done ? 'done' : 'not done'}</span>
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </nav>
                    <div className="re__scroll" ref={scroller} onScroll={spy}>
                        <header className="re__mast">
                            <span className="re__rings tk__figure"><TokenFace size={56} /></span>
                            <h2 className="gp-explain__h re__h">
                                {made ? <>Copy <b>{shown}</b>’s token.</> : shown ? <>Make <b>{shown}</b>.</> : 'Make a token.'}
                            </h2>
                            {made ? (
                                <p className="re__lead">
                                    This is the only time it is shown. Whatever holds it reaches {realmsLine} as you allowed, until {dateOf(new Date(made.access_token?.expires_at || until))}.
                                </p>
                            ) : (<>
                                <p className="re__lead">
                                    A token is a key for software you run {inGroup ? 'for ' + spaceName : 'yourself'}, such as Claude Code or a script. It reaches only the realms you pick, does only what you allow there, and stops when it expires.
                                </p>
                                <p className="re__lead">
                                    It is a contract like an app’s: listed under Tokens in Contracts, where you can pause or revoke it, and everything it writes carries its name.
                                </p>
                            </>)}
                        </header>

                        {made ? <Reveal token={made.token} groupId={groupId} /> : (<>
                            <div className="re__step" data-step="1">
                                <Section n="01" title="Name it" hint="Name it for what will use it: Claude Code on your laptop, an export script. Its writes carry this name.">
                                    <label className="re__field is-name">
                                        <span className="re__minor">Name</span>
                                        <input value={name} maxLength={100} placeholder="Claude Code · MacBook" aria-label="Name"
                                            onChange={(e) => setName(e.target.value)} autoFocus={!preset.name} />
                                    </label>
                                </Section>
                            </div>

                            <div className="re__step" data-step="2">
                                <Section n="02" title="Realms" aside={picked.length ? `${picked.length} added` : null}
                                    hint={`The realms it reaches, as on a consent page: add one or more. It never sees anything else of ${space}.`}>
                                    {offered.length ? (
                                        <RealmChooser realms={offered} selectedIds={realmIds} onSelect={toggleRealm} />
                                    ) : <p className="re__empty">{inGroup ? `${spaceName} has no realm that holds nodes.` : 'You have no realm that holds nodes.'} Make one in Realms first.</p>}
                                </Section>
                            </div>

                            <div className="re__step" data-step="3">
                                <Section n="03" title="What it may do"
                                    hint={picked.length ? 'What it may do with the nodes in its realms. Tap a verb to turn it off or on.' : 'Add a realm first.'}>
                                    {picked.length > 0 && (
                                        <div className="gp-bound re__rows">
                                            <div className={`re__row${chosen.length ? '' : ' is-idle'}`}>
                                                <div className="gp-brow is-lit">
                                                    <span className="nx-rules__icon"><Glyphed size={22}><LayoutGrid size={11} strokeWidth={1.8} /></Glyphed></span>
                                                    <span className="gp-brow__lab">Nodes in {realmsLine}</span>
                                                    <span className="re__verbs" role="group" aria-label="What it may do">
                                                        {offeredVerbs.map((v) => {
                                                            const held = !allowed.includes(v);
                                                            const on = !held && verbs.has(v);
                                                            return (
                                                                <button key={v} type="button" aria-pressed={on} disabled={held}
                                                                    title={held ? `${v}: its realms do not allow it` : `${v}: ${VERB_SAYS[v]}`}
                                                                    className={`gp-vtag is-${v} re__vtag${on ? ' is-on' : ''}`} onClick={() => toggle(v)}>{v}</button>
                                                            );
                                                        })}
                                                    </span>
                                                </div>
                                                <p className="nx-rules__more">
                                                    {chosen.length ? `It may ${listOf(chosen.map((v) => VERB_SAYS[v]))}.` : 'Nothing yet. Tap a verb.'}
                                                    {barred.length > 0 && ` ${picked.length > 1 ? 'Its realms do' : `${realmsLine} does`} not allow ${listOf(barred, 'or')}.`}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </Section>
                            </div>

                            <div className="re__step" data-step="4">
                                <Section n="04" title="How long" hint="It stops working then. A shorter one is safer for a one-off.">
                                    <Segmented label="How long" value={hours} onChange={setHours} options={TOKEN_TERMS} />
                                </Section>
                            </div>

                            <div className="re__step" data-step="5">
                                <Section n="05" title="Check it" hint="What it reaches, as it will be granted.">
                                    <div className="re__check">
                                        <div className="re__check-head"><b>{shown || 'Unnamed token'}</b><span>{picked.length ? `through ${realmsLine}` : 'no realm yet'}</span></div>
                                        {picked.length > 1 ? picked.map((r) => (
                                            <React.Fragment key={r.id}>
                                                <div className="gp-rcpt__grp re__sub">From {r.name}</div>
                                                <AccessList access={reachOf(r, chosen)} schemaNames={schemaNames} loadTitles={calls.nodeTitles}
                                                    empty="Nothing, with what it may do." />
                                            </React.Fragment>
                                        )) : (
                                            <AccessList access={reachOf(picked[0], chosen)} schemaNames={schemaNames} loadTitles={calls.nodeTitles}
                                                empty={picked.length ? 'Nothing yet. Choose what it may do.' : 'Nothing yet. Add a realm.'} />
                                        )}
                                        <p className="re__check-foot">
                                            For {term.label}, until {dateOf(until)}.{inGroup ? ` It reaches ${spaceName}’s graph, never yours.` : ''}
                                        </p>
                                    </div>
                                </Section>
                            </div>
                        </>)}
                    </div>
                </div>
                {error && <p className="re__error">{error}</p>}
                <Decide
                    className="re__decide"
                    denyLabel={made ? 'Done' : 'Cancel'}
                    onDeny={close}
                    confirmLabel={made ? null : working ? 'Making…' : 'Make token'}
                    disabled={!ready || working}
                    onConfirm={make}
                />
            </ConsentFrame>
        </Modal>
    );
}

// The token, this once; how to keep it out of a chat (in the Keychain, where a script
// reads it, so it never lands in a transcript); and how to use it, ready to paste, for
// Claude Code, a Python script or curl. Anyone can make a token, so this says it all.
const USES = [{ id: 'claude', label: 'Claude Code' }, { id: 'python', label: 'Python' }, { id: 'curl', label: 'curl' }];

// `guide` is the token guide as plain markdown (ecosystem_developer_docs/tokens.md,
// served by /docs/guides/tokens), which says what to send: Claude Code reads it there.
export function usesOf(nodes, guide) {
    const read = 'security find-generic-password -s ofself-token -w';
    return {
        claude: `My ofSelf access token is in my macOS Keychain, as the service ofself-token. Never ask me for it, print it or write it to a file. In any script you write, read it with \`${read}\` and send it in the Authorization header, as "Bearer " followed by the token. My nodes are at ${nodes}. Read ${guide} for what to send.`,
        python: `import subprocess, requests\n\ntoken = subprocess.run(\n    ['security', 'find-generic-password', '-s', 'ofself-token', '-w'],\n    capture_output=True, text=True, check=True).stdout.strip()\nnodes = requests.get('${nodes}',\n                     headers={'Authorization': f'Bearer {token}'}).json()`,
        curl: `TOKEN=$(${read})\ncurl -H "Authorization: Bearer $TOKEN" ${nodes}`,
    };
}

function Reveal({ token, groupId }) {
    const [use, setUse] = useState('claude');
    const nodes = `${apiBase()}${groupId ? `/groups/${groupId}` : ''}/nodes`;
    const keep = `security add-generic-password -U -a "$USER" -s ofself-token -w '${token}'`;
    const shown = usesOf(nodes, `${apiBase()}/docs/guides/tokens`)[use];
    return (<>
        <div className="re__step" data-step="6">
            <Section n="01" title="The token" actions={<CopyButton text={token} label="Copy token" />}>
                <pre className="re__yaml-out tk__secret" aria-label="The token">{token}</pre>
            </Section>
        </div>
        <div className="re__step">
            <Section n="02" title="Keep it safe" actions={<CopyButton text={keep} label="Copy" />}
                hint="On a Mac, keep it in your Keychain: not in a chat or a file Claude Code reads, so it never lands in a transcript. Elsewhere, use your system’s own secret store.">
                <pre className="re__yaml-out">{keep}</pre>
            </Section>
        </div>
        <div className="re__step">
            <Section n="03" title="Use it" actions={<CopyButton text={shown} label="Copy" />}
                hint={use === 'claude' ? 'The token is not in it: Claude Code’s scripts read it from your Keychain. Paste it into Claude Code, or into your project’s CLAUDE.md so every session knows.' : 'A first read of your nodes, to build on.'}>
                <Segmented label="Use it with" value={use} onChange={setUse} options={USES} />
                <pre className="re__yaml-out tk__use" aria-label="How to use it">{shown}</pre>
            </Section>
        </div>
    </>);
}

function CopyButton({ text, label }) {
    const [done, setDone] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setDone(true);
            setTimeout(() => setDone(false), 1600);
        } catch {
            setDone(false);
        }
    };
    return (
        <button type="button" className="re__btn tk__copy" onClick={copy}>
            {done ? <Check size={13} strokeWidth={2.2} aria-hidden="true" /> : <Copy size={13} strokeWidth={1.8} aria-hidden="true" />}
            <span>{done ? 'Copied' : label}</span>
        </button>
    );
}
